import {
  type NodeDocumentRow,
  nodeDocumentRows,
} from 'features/knowledgeDocumentViewer/components/sidebar/relatedDocumentRows'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useNodePageDestinations } from 'features/knowledgeDocumentViewer/graph/useNodePageDestinations'
import { useSystemDocumentsByBqIds } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import { useMemo } from 'react'

import type { LotoReportScope } from './lotoReportSchemas'

export type LotoTargetDrawing = NodeDocumentRow & { isHere: boolean }

export function useLotoTargetDrawings(
  scope: LotoReportScope,
  nodeId: string
): {
  drawings: LotoTargetDrawing[]
  isLoading: boolean
  error: Error | undefined
} {
  const { documentId, pageNumber } = useDocumentTabDataContext()
  const { organization, site, pipelineConfig } = scope
  const node = useMemo(
    () => ({
      id: nodeId,
      tags: {
        sandbox: 'sandbox' as const,
        organization,
        site,
        'pipeline-config': pipelineConfig,
      },
    }),
    [nodeId, organization, site, pipelineConfig]
  )
  const pages = useNodePageDestinations(node)
  const documentIds = useMemo(
    () => (pages.destinations ?? []).map((page) => page.documentId),
    [pages.destinations]
  )
  const documents = useSystemDocumentsByBqIds(documentIds)
  const drawings = useMemo(
    () =>
      nodeDocumentRows(
        pages.destinations ?? [],
        documents.documents ?? [],
        documentId
      ).map((row) => ({
        ...row,
        isHere: row.isCurrent && row.pageNumber === pageNumber,
      })),
    [pages.destinations, documents.documents, documentId, pageNumber]
  )
  return {
    drawings,
    isLoading: pages.isLoading || documents.isLoading,
    error: pages.error,
  }
}
